import defaults from 'lodash/defaults';
import defaultsDeep from 'lodash/defaultsDeep';
import {Component} from 'react';
import {ReactModal} from '../../components/modals/ReactModal';
import {ReactReduxModal} from '../react-redux-modals';
import {ReactReduxModalHOC} from '../react-redux-modals/components/ReactReduxModalHOC';
import {ReduxRequest} from '../redux-request';
import {buildReportActions} from './actions';
import {ExportModalHOC, modalOptions} from './components/ExportModalHOC';
import {buildReportFactory, defaultGetReportPath} from './defaultReportFactory';
import {ReduxObservableListExportsEpics} from './epics';
import {buildReportReducer} from './reducers';
import {BASE_MODAL_CONFIG, BASE_OPTIONS, validateModalConfig, validateOptions} from './utils';


/**
 * @param {ReduxObservableListExportsOptions} o
 * @constructor
 */
export function ReduxListExports(o) {
  // defaults is applied so that only specific child objects have any defaults applied explicitly.
  // Do not use defaultsDeep!

  /** @type {ReduxObservableListExportsOptions} */
  const options = {};
  defaults(options, o, BASE_OPTIONS);
  if (options.modalOptions) {
    const mergedModalOptions = {};
    defaultsDeep(mergedModalOptions, options.modalOptions, modalOptions);
    options.modalOptions = mergedModalOptions;
  } else {
    options.modalOptions = modalOptions;
  }

  if (!options.reportPath)
    options.reportPath = defaultGetReportPath(options);

  if (!options.reportFactory)
    options.reportFactory = buildReportFactory(options);

  // Modify the buildRequest option to inject the format
  const buildRequest = options.buildRequest;
  options.buildRequest = state => ({
    format: state.format,
    ...buildRequest(state),
  });

  validateOptions(options);
  const reactReduxModalOptions = {
    prefix: options.prefix,
    getState: options.getReportState,
    modalOptions: options.modalOptions,
    logger: options.logger,
  };

  const reactReduxModal = new ReactReduxModal(reactReduxModalOptions);

  const reduxRequestOptions = {
    prefix: options.prefix,
    getState: options.getReportState,
    requestFactory: options.reportFactory,
    buildRequest: options.buildRequest,
    getResponseStatus: options.getResponseStatus,
    logger: options.logger,
  };
  const reduxRequest = new ReduxRequest(reduxRequestOptions);

  this.options = options;
  this.actions = buildReportActions(reactReduxModal, reduxRequest, options);
  this.reducers = buildReportReducer(reactReduxModal, reduxRequest, this.actions, options);
  this.epics = new ReduxObservableListExportsEpics(this.actions, options);
  this.epics.addEpics(reactReduxModal.epics.epics);
  this.epics.addEpics(reduxRequest.epics.epics);

  const modalConfig = {
    actions: this.actions,
    getState: this.options.getReportState,
    logger: this.options.logger,
  };
  /** @type {ExportModalHOCOptions} */
  const exportConfig = {
    actions: this.actions,
    getState: this.options.getState,
    getReportState: this.options.getReportState,
    logger: this.options.logger,
  };
  defaults(exportConfig, options.exportConfig || {}, BASE_MODAL_CONFIG);
  options.exportConfig = exportConfig;
  validateModalConfig(options.exportConfig);

  reactReduxModalOptions.modalComponent = this.options.modalComponent = this.modalComponent = ReactReduxModalHOC(
    ExportModalHOC(this.options.modalComponent, exportConfig, this.options),
    modalConfig,
    ReactModal,
  );
}

/**
 * @typedef {ReduxObservableListOptions} ReduxObservableListExportsOptions
 * @property {Function} getReportState Retrieve report state from root state.

 * @property {String|ReduxObservableListOptions~listPath} reportPath
 * @property {Function} getReportParams Insert params to the report request. Do not include outer ampersands
 * @property {AjaxFactory} [reportFactory]
 * @property {Function} buildRequest Build the request from the redux state
 * @property {Function} getResponseStatus Determine if the response is empty or not and return the appropriate status.
 * @property {Function} modifyResponse Post process the response data
 * @property {Function} getFileName Provide file name given request data
 *
 * @property {Component} modalComponent The react component to render in a modal
 * @property {ExportModalHOCOptions} exportConfig Options passed to Export Modal higher order component
 * @property {ModalOptions} [modalOptions] Options passed to modal manager
 * @property {Number} [modalCloseDelay=2000] Delay closing the modal after the download has been started in milliseconds.
 */

/**
 * @callback {Function} ReduxObservableListExportsOptions~getReportState
 * @param {Object} rootState
 * @return {ReduxObservableListExportsState}
 */

/**
 * @typedef {Object} ExportModalHOCOptions
 * @property {Function} getState The ReduxList getState option
 * @property {Function} getReportState Retrieve report state from root state
 * @property {Boolean} [useListStateFilter=true] Retrieve the default filter state from the current list state using options.getState
 * @property {Function<FilterData>} [getDefaultFilterData] Provide the default filter options. Required if useListStateFilter=false.
 */



// WEBPACK FOOTER //
// ./src/js/core-utilities/redux-observable-list-exports/index.js